import { ExternalLink, ImagePlay, PackageCheck, Plus, ReceiptText, Store } from "lucide-react";
import type { ComponentType, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { usePermissions } from "@/lib/auth/usePermissions";
import { env } from "@/lib/env";
import type { Permission } from "@tcg/api-client";

import { orderViewLink } from "@/features/orders/orderViews";

interface Action {
  key: "add_card" | "view_orders" | "ship" | "open_pos";
  to: string;
  external?: boolean;
  permission: Permission;
  Icon: ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
}

/** design/04 §0: 「カードを登録」「注文を見る」「発送する」「レジを開く」. */
export const BIG_ACTIONS: readonly Action[] = [
  { key: "add_card", to: "/products/new", permission: "products.write", Icon: Plus },
  { key: "view_orders", to: "/orders", permission: "orders.read", Icon: ReceiptText },
  {
    key: "ship",
    // 発送する → the orders packed and waiting for the carrier (T11-22 view 発送待ち)
    to: orderViewLink("ready_to_ship"),
    permission: "orders.fulfill",
    Icon: PackageCheck,
  },
  { key: "open_pos", to: env.VITE_POS_URL, external: true, permission: "pos.sell", Icon: Store },
];

/** T09-30 quick action: the editor opens on the live design with the homepage banner chosen. */
const EDIT_BANNER_TO = "/online-store/themes/live/editor?page=index&select=hero";

const BUTTON =
  "relative flex h-full min-h-24 w-full flex-col items-start justify-center gap-1 rounded-lg border-2 border-foreground bg-background p-4 text-left shadow-brand transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none md:min-h-28";

/**
 * The four big buttons on top of the home page (T11-10): ≥ 96 px tall on tablets, words in the
 * staff member's language; the register opens in a new tab (its own app). A button the account
 * cannot use is not shown (the shell does the same for the menu).
 */
export function BigActions({
  badges = {},
}: {
  badges?: Partial<Record<Action["key"], ReactNode>>;
}) {
  const { t } = useTranslation();
  const perms = usePermissions();
  const actions = BIG_ACTIONS.filter((action) => perms.can(action.permission));
  return (
    <nav
      aria-label={t("dashboard:big_actions")}
      data-slot="big-actions"
      className="flex flex-col gap-3"
    >
      <ul className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {actions.map(({ key, to, external, Icon }) => {
          const body = (
            <>
              <Icon className="size-7" aria-hidden />
              <span className="text-lg leading-tight font-bold">{t(`home.${key}`)}</span>
              <span className="text-sm text-muted-foreground">{t(`home.${key}_hint`)}</span>
              {external ? (
                <ExternalLink
                  className="absolute top-3 right-3 size-4 text-muted-foreground"
                  aria-hidden
                />
              ) : null}
              {badges[key] ?? null}
            </>
          );
          return (
            <li key={key} data-action={key}>
              {external ? (
                <a
                  href={to}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={BUTTON}
                  aria-label={`${t(`home.${key}`)} ${t("nav.external")}`}
                >
                  {body}
                </a>
              ) : (
                <Link to={to} className={BUTTON}>
                  {body}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
      {perms.can("cms.edit") ? (
        <Link
          to={EDIT_BANNER_TO}
          data-action="edit_banner"
          className="inline-flex min-h-touch items-center gap-2 self-start rounded-md border px-3 text-sm font-medium hover:bg-accent"
        >
          <ImagePlay className="size-5" aria-hidden />
          <span>
            {t("home.edit_banner")}
            <span className="block text-xs font-normal text-muted-foreground">
              {t("home.edit_banner_hint")}
            </span>
          </span>
        </Link>
      ) : null}
    </nav>
  );
}
